import React, { useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";

import EditIcon from "@mui/icons-material/EditOutlined";
import { useTable } from "../common/use-tableHandler";
import { Link, useResolvedPath, useNavigate } from "react-router-dom";
import { notBlank } from "../util/ext";
import ListSummaryCard from "../creator-application/common/ListSummaryCard";
import { map, uniq } from "ramda";
import UserCard from "./UserCard";
import YesNoCard from "../components/YesNoCard";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },

  container: {
    height: "calc(100vh - 350px)",
  },

  contentCell: {
    width: "25%",
  },

  cell: {
    minWidth: 100,
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  selectCell: {
    padding: theme.spacing(0),
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
  },
}));

const UserTable = props => {
  const url = useResolvedPath(".").pathname;
  const navigate = useNavigate();

  const {
    userList,
    page,
    onPageChange,
    total = 0,
    orderBy = "accountName",
    order = "asc",
    onSortChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const { handleRequestSort, handleChangePage } = useTable(
    {
      onSortChange,
      onPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl} className={classes.container}>
        <Table stickyHeader aria-label="Content">
          <TableHead>
            <TableRow>
              <TableCell
                key="accountName"
                align="left"
                className={classes.contentCell}
              >
                <TableSortLabel
                  active={orderBy === "accountName"}
                  direction={order}
                  onClick={() => handleRequestSort("accountName")}
                >
                  Username
                  {orderBy === "accountName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="emailAddress"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "emailAddress"}
                  direction={order}
                  onClick={() => handleRequestSort("emailAddress")}
                >
                  Email Address
                  {orderBy === "emailAddress" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="organizations"
                align="left"
                className={classes.cell}
              >
                Organizations
              </TableCell>

              <TableCell key="loginCount" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "loginCount"}
                  direction={order}
                  onClick={() => handleRequestSort("loginCount")}
                >
                  Login Count
                  {orderBy === "loginCount" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="active" align="left" className={classes.cell}>
                Active
              </TableCell>

              <TableCell
                key="email-address-verified"
                align="left"
                className={classes.cell}
              >
                Verified Email
              </TableCell>

              <TableCell
                key="actions-edit"
                align="center"
                style={{ width: 100 }}
              >
                Edit
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {userList.map(user => (
              <TableRow hover tabIndex={-1} key={user.userId} role="checkbox">
                <TableCell
                  key={"userId:" + user.id}
                  align="left"
                  className={classes.contentCell}
                >
                  <UserCard user={user} />
                </TableCell>

                <TableCell key={"email-address:" + user.userId} align="left">
                  {user.emailAddress}
                </TableCell>

                <TableCell key={"anthologies:" + user.userId} align="left">
                  <ListSummaryCard
                    data={uniq(
                      map(
                        org => ({
                          id: org.organizationId,
                          name: org.organizationName,
                          handleClick: () =>
                            navigate(
                              `/manage-organizations/organizations/${org.organizationId}`,
                              {
                                state: {
                                  label: "Back to Browse Users",
                                  previousLocation: "/user-admin/users",
                                },
                              },
                            ),
                        }),
                        user.organizations,
                      ),
                    )}
                  />
                </TableCell>

                <TableCell
                  key={"login-count:" + user.userId}
                  align="left"
                  className={classes.contentTypeCell}
                >
                  {user.loginCount}
                </TableCell>

                <TableCell key={"active:" + user.userId} align="left">
                  <YesNoCard value={user.active} />
                </TableCell>

                <TableCell
                  key={"email-address-verified:" + user.userId}
                  align="left"
                >
                  <YesNoCard
                    value={user.emailAddressVerified}
                    showNoIcon={false}
                  />
                </TableCell>

                <TableCell
                  key={"actions-edit:" + user.userId}
                  align="center"
                  style={{ width: 100 }}
                >
                  <IconButton
                    className={classes.button}
                    aria-label="edit"
                    size="large"
                  >
                    <Link to={`${url}/${user.userId}`}>
                      <EditIcon />
                    </Link>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[25]}
        component="div"
        count={total}
        rowsPerPage={25}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default UserTable;
